import React from 'react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Tooltip,
  Button,
  ToggleButton,
  Slider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import {
  Info as InfoIcon,
  AddAPhoto,
  Delete,
  CheckCircle,
  Cancel,
  LocationOn as LocationIcon,
  Web as WebIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import { Editor } from 'react-draft-wysiwyg';
import SimpleMapCoordinateSelector from 'components/molecules/MapCoordinateSelector';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import {
  HoneyGeneralContainer,
  HoneyGeneralHeader,
  HoneyGeneralTitle,
  HoneyGeneralSubtitle,
  HoneySectionContainer,
  HoneyTextField,
  HoneyEditorContainer,
  HoneySectionTitle,
  HoneyImageCard,
  HoneyImageUploadArea,
  HoneyImagePreview,
  HoneyImageDeleteButton,
  HoneyStatusToggle,
  HoneySliderContainer,
  HoneySliderValue,
  HoneySaveButton,
  HoneyActionsContainer,
  HoneyAlert,
  HoneyMapContainer,
  HoneyDivider
} from './styled';

const GeneralInfoView = ({
  // Form data
  formData,
  loading,
  error,
  
  // Editor states
  descriptionEditorState,
  accessEditorState,
  
  // Image states
  imagePreview,
  
  // Handlers
  handleInputChange,
  handleSliderChange,
  handleStatusChange,
  handleEditorStateChange,
  handleAccessEditorStateChange,
  handleImageChange,
  clearImageSelection,
  handleCoordinatesChange,
  handleSubmit,
  
  // Helper functions
  getStatusInfo,
  renderImageUploadCard,
  
  // External props
  apiKey,
  t
}) => {
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const handlePreviewOpen = () => {
    if (imagePreview) {
      setPreviewOpen(true);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const renderImageUploadCardWithPreview = (title, preview, onChange, onClear, id) => {
    return (
      <HoneyImageCard>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        {/* Hidden file input - always present for replacing image */}
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id={id}
          type="file"
          onChange={onChange}
        />
        
        {preview ? (
          <Box position="relative">
            <label htmlFor={id} style={{ cursor: 'pointer', display: 'block' }}>
              <img
                src={preview}
                alt={title}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '2px solid #e0e0e0',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              />
            </label>
            <Box 
              position="absolute" 
              top={8} 
              right={8} 
              display="flex" 
              gap={1}
            >
              <IconButton
                onClick={handlePreviewOpen}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                }}
              >
                <ZoomInIcon />
              </IconButton>
              <label htmlFor={id}>
                <IconButton
                  component="span"
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                    color: 'primary.main'
                  }}
                >
                  <AddAPhoto />
                </IconButton>
              </label>
            </Box>
            {/* Helper text */}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ display: 'block', mt: 1, textAlign: 'center' }}
            >
              Click image or camera icon to change
            </Typography>
          </Box>
        ) : (
          <HoneyImageUploadArea>
            <label htmlFor={id}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddAPhoto />}
                fullWidth
                sx={{ height: 200 }}
              >
                Upload {title}
              </Button>
            </label>
          </HoneyImageUploadArea>
        )}
      </HoneyImageCard>
    );
  };
  return (
    <HoneyGeneralContainer maxWidth="md">
      <HoneyGeneralHeader>
        <HoneyGeneralTitle>
          <InfoIcon fontSize="large" />
          General Information
        </HoneyGeneralTitle>
        <HoneyGeneralSubtitle>
          Configure your venue's basic information and settings
        </HoneyGeneralSubtitle>
      </HoneyGeneralHeader>

      {error && (
        <HoneyAlert severity="error" sx={{ mb: 3 }}>
          {error}
        </HoneyAlert>
      )}

      {/* Basic Information Section */}
      <HoneySectionContainer id="venue-section-basic">
        <HoneySectionTitle>
          <InfoIcon />
          Basic Information
        </HoneySectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HoneyTextField
              name="name"
              label="Venue Name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              helperText="The main name of your venue"
            />
          </Grid>
          
          <Grid item xs={12}>
            <HoneyTextField
              name="description"
              label="Short Description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              inputProps={{ maxLength: 100 }}
              helperText="Brief description (max 100 characters)"
            />
          </Grid>
        </Grid>
      </HoneySectionContainer>

      {/* Status Section */}
      <HoneySectionContainer>
        <FormControl component="fieldset" fullWidth>
          <HoneySectionTitle>
            <SettingsIcon />
            Venue Status
          </HoneySectionTitle>

          <HoneyStatusToggle
            value={formData.status}
            exclusive
            onChange={handleStatusChange}
            aria-label="venue status"
            fullWidth
          >
            <Tooltip title="Fully functional site - venue is visible and bookable by users" arrow placement="top">
              <ToggleButton value="AC" aria-label="active">
                <CheckCircle sx={{ mr: 1 }} />
                Active
              </ToggleButton>
            </Tooltip>
            <Tooltip title="We don't have consent from the venue, but we are showing general marketing information - users cannot book through us" arrow placement="top">
              <ToggleButton value="PA" aria-label="pre-approved">
                <InfoIcon sx={{ mr: 1 }} />
                Pre-Approved
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Won't be shown in the platform listing - venue is hidden from users" arrow placement="top">
              <ToggleButton value="IN" aria-label="inactive">
                <Cancel sx={{ mr: 1 }} />
                Inactive
              </ToggleButton>
            </Tooltip>
          </HoneyStatusToggle>

          <HoneyAlert
            severity={getStatusInfo(formData.status).color}
            variant="outlined"
            sx={{ mt: 2 }}
          >
            Current status: <strong>{getStatusInfo(formData.status).label}</strong>
            {formData.status === 'AC' && ' - Venue is visible and bookable by users'}
            {formData.status === 'PA' && ' - Venue is visible but requires direct booking through website'}
            {formData.status === 'IN' && ' - Venue is hidden from users and not bookable'}
            {formData.status === 'PP' && ' - Venue is pending review and not yet active'}
          </HoneyAlert>
        </FormControl>
      </HoneySectionContainer>

      {/* Full Description Section */}
      <HoneySectionContainer id="venue-section-fulldesc">
        <HoneySectionTitle>Full Description</HoneySectionTitle>
        <HoneyEditorContainer>
          <Editor
            editorState={descriptionEditorState}
            onEditorStateChange={handleEditorStateChange}
            wrapperClassName="wysiwyg-wrapper"
            editorClassName="wysiwyg-editor"
            toolbar={{
              options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'history'],
            }}
          />
        </HoneyEditorContainer>
      </HoneySectionContainer>

      {/* Location Section */}
      <HoneySectionContainer id="venue-section-location">
        <HoneySectionTitle>
          <LocationIcon />
          Location & Address
        </HoneySectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HoneyTextField
              name="single_address"
              label="Address"
              value={formData.single_address}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              helperText="Full address of your venue"
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Set the exact location of your venue on the map. You can search for an address, 
            use your current location, or click/drag the marker to position it accurately.
          </Typography>
          
          <HoneyMapContainer>
            <SimpleMapCoordinateSelector
              initialCoordinates={
                formData.latitude && formData.longitude
                  ? { lat: formData.latitude, lng: formData.longitude }
                  : undefined
              }
              initialAddress={formData.single_address}
              onCoordinatesChange={handleCoordinatesChange}
              height={400}
              apiKey={apiKey}
            />
          </HoneyMapContainer>
        </Box>
      </HoneySectionContainer>

      <HoneyDivider />

      {/* Contact Information Section */}
      <HoneySectionContainer id="venue-section-contact">
        <HoneySectionTitle>
          <PhoneIcon />
          Contact Information
        </HoneySectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="website"
              label="Website"
              value={formData.website}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: <WebIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="contact_email"
              label="Contact Email"
              value={formData.contact_email}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="contact_phone_1"
              label="Primary Phone"
              value={formData.contact_phone_1}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="contact_phone_2"
              label="Secondary Phone"
              value={formData.contact_phone_2}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
        </Grid>
      </HoneySectionContainer>

      {/* Access Instructions Section */}
      <HoneySectionContainer>
        <HoneySectionTitle>Access Instructions</HoneySectionTitle>
        <HoneyEditorContainer>
          <Editor
            editorState={accessEditorState}
            onEditorStateChange={handleAccessEditorStateChange}
            wrapperClassName="wysiwyg-wrapper"
            editorClassName="wysiwyg-editor"
            toolbar={{
              options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'history'],
            }}
          />
        </HoneyEditorContainer>
      </HoneySectionContainer>

      {/* Social Media Section */}
      <HoneySectionContainer>
        <HoneySectionTitle>
          <ShareIcon />
          Social Media Links
        </HoneySectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="twitter_url"
              label="X (Twitter) URL"
              value={formData.twitter_url}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="facebook_url"
              label="Facebook URL"
              value={formData.facebook_url}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="instagram_url"
              label="Instagram URL"
              value={formData.instagram_url}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <HoneyTextField
              name="linkedin_url"
              label="LinkedIn URL"
              value={formData.linkedin_url}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
        </Grid>
      </HoneySectionContainer>

      {/* Booking Settings Section */}
      <HoneySectionContainer>
        <HoneySectionTitle>Booking Settings</HoneySectionTitle>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Set the percentage of occupancy available for instant booking. 0% means all bookings 
          need confirmation, 100% means all bookings are automatically approved.
        </Typography>

        <HoneySliderContainer>
          <Slider
            value={formData.occupancy_instant_booking * 100}
            onChange={handleSliderChange}
            aria-labelledby="occupancy-slider"
            valueLabelDisplay="off"
            step={5}
            marks={[
              { value: 0, label: '0%' },
              { value: 25, label: '25%' },
              { value: 50, label: '50%' },
              { value: 75, label: '75%' },
              { value: 100, label: '100%' }
            ]}
          />
        </HoneySliderContainer>

        <HoneySliderValue>
          Current value: <strong>{(formData.occupancy_instant_booking * 100).toFixed(0)}%</strong> of total occupancy
        </HoneySliderValue>
      </HoneySectionContainer>

      {/* Image Uploads Section */}
      <HoneySectionContainer id="venue-section-image">
        <HoneySectionTitle>Venue Images</HoneySectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {renderImageUploadCardWithPreview(
              "Main Image",
              imagePreview,
              handleImageChange,
              clearImageSelection,
              "image-upload"
            )}
          </Grid>
        </Grid>
      </HoneySectionContainer>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Main Image Preview</Typography>
            <IconButton onClick={handlePreviewClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {imagePreview && (
            <Box textAlign="center">
              <img
                src={imagePreview}
                alt="Main Image Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Button */}
      <HoneyActionsContainer>
        <HoneySaveButton
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <InfoIcon />}
        >
          {loading ? 'Saving Changes...' : '💾 Save General Information'}
        </HoneySaveButton>
      </HoneyActionsContainer>
    </HoneyGeneralContainer>
  );
};

export default GeneralInfoView;